import React, {useEffect, useMemo, useState} from 'react';
import {useStrings} from '@zvne/fe-framework/dist/presentation/language/strings';
import {useSubmitApi} from '@zvne/fe-framework/dist/data-access/use-submit-api';
import {DateTimeFormGroup} from '@zvne/fe-framework/dist/presentation/form/form-group/date-time-form-group';
import {
    LocalizedSchemaDefinition,
    useValidationSchema
} from '@zvne/fe-framework/dist/presentation/form/use-validation-shema';
import {NumberFormGroup} from '@zvne/fe-framework/dist/presentation/form/form-group/number-form-group';
import {
    ClusterHistoryParams,
    ClusterHistoryProperties,
    getClusterHistory
} from '../../../data-access/cluster-history';
import {DateTimePickerMode} from '@zvne/fe-framework/dist/presentation/form/input/date-time-picker';
import {NullGuard} from '@zvne/fe-framework/dist/presentation/map/guards/null-guard';
import {MapLoadingGuard} from '@zvne/fe-framework/dist/presentation/map/guards/map-loading-guard';
import {MapEventListener} from '@zvne/fe-framework/dist/presentation/map/misc/map-event-listener';
import {SubmitButton} from '@zvne/fe-framework/dist/presentation/form/submit-button';
import {numberToUserString, dateToUserString, formatDate} from '@zvne/fe-framework/dist/utilities/type-conversion';
import {getEmptyFeatureCollection} from '@zvne/fe-framework/dist/utilities/geometry';
import {strings} from './strings';
import {Formik} from 'formik';
import {MapboxMap} from '../../reusable/map/map';
import {useLanguage} from '@zvne/fe-framework/dist/presentation/language/language';
import {Point, Polygon} from 'geojson';
import {Layer, MapContext, Popup} from 'react-mapbox-gl';
import {useTheme} from '@zvne/fe-framework/dist/presentation/theme/theme-context';
import {LegendControl} from '@zvne/fe-framework/dist/presentation/map/controls/legend-control';
import {DropdownControl} from '@zvne/fe-framework/dist/presentation/map/controls/dropdown-control';
import {CheckboxDropdownItem} from '@zvne/fe-framework/dist/presentation/form/input/checkbox-dropdown-item';
import {SimpleGeoJSONSource} from '../../reusable/map/simple-geojson-source';
import {configuration} from '../../../config/configuration';
import {Form} from 'react-bootstrap';
import * as Yup from 'yup';

const minDate = new Date('2009-01-01T00:00:00Z');

function getInitialStartTimestamp(): Date {
    if (configuration.environment === 'dev') {
        return new Date('2020-01-18T10:30:00Z');
    }
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
}

const schema: LocalizedSchemaDefinition<ClusterHistoryParams> = s => ({
    startTimestamp: Yup.date()
        .defined()
        .typeError(s.date)
        .required(s.required)
        .min(minDate, s.dateMin)
    ,
    durationMinutes: Yup.number()
        .defined()
        .typeError(s.number)
        .integer(s.integer)
        .required(s.required)
        .min(0, s.numberMin)
        .max(1440, s.numberMax)
});

const isDateValid = (date: Date) => date >= minDate;

const emptyInterval = {
    polygons: getEmptyFeatureCollection<Polygon, ClusterHistoryProperties>(),
    centroids: getEmptyFeatureCollection<Point, ClusterHistoryProperties>()
};

export function HistoryScreen() {
    const [clusterHistory, handleSubmit] = useSubmitApi(getClusterHistory);

    const [selectedMinute, setSelectedMinute] = useState(0);
    useEffect(() => {
        setSelectedMinute(0);
    }, [clusterHistory]);

    const [selectedInterval, intervalStart, intervalEnd] = useMemo(() => {
        if (typeof clusterHistory !== 'object') {
            return [null, null, null];
        }
        const end = new Date(clusterHistory.startTimestamp);
        end.setMinutes(end.getMinutes() + selectedMinute);
        const start = new Date(end);
        start.setMinutes(end.getMinutes() - clusterHistory.intervalSizeMinutes);
        const interval = clusterHistory.intervals.get(end.valueOf());
        return [interval, start, end];
    }, [clusterHistory, selectedMinute]);

    const [sliderHoldPosition, setSliderHoldPosition] = useState<number | null>(null);

    const [arePredictionsEnabled, setPredictionsEnabled] = useState([true, true, false, false]);
    const [selectedCluster, setSelectedCluster] = useState<ClusterHistoryProperties | null>(null);

    const language = useLanguage();
    const s = useStrings(strings);
    const intervalMessage = useMemo(() => {
        if (intervalStart == null || intervalEnd == null) {
            return null;
        }
        const dateString = dateToUserString(intervalStart, language);
        const startTime = formatDate(intervalStart, 'HH:mm');
        const endTime = formatDate(intervalEnd, 'HH:mm');
        return `${dateString} ${startTime} - ${endTime}`;
    }, [intervalStart, intervalEnd, language]);

    const theme = useTheme();
    const realClusterColor = theme === 'dark' ? '#00ff00' : '#008800';
    const predictedClusterColor = theme === 'dark' ? '#ffff00' : '#888800';

    const validationSchema = useValidationSchema(schema);

    return (
        <div style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
            <Formik
                initialValues={{
                    startTimestamp: getInitialStartTimestamp(),
                    durationMinutes: 1440
                }}
                onSubmit={handleSubmit}
                validationSchema={validationSchema}
            >
                {f =>
                    <form onSubmit={f.handleSubmit}>
                        <div className={'row'}>
                            <DateTimeFormGroup
                                formik={f}
                                label={s.startTimestamp}
                                name={'startTimestamp'}
                                mode={DateTimePickerMode.DateTimeShort}
                                isDateEnabled={isDateValid}
                                disableValidStyling
                                layout={(labelContainer, fieldContainer, errorTextContainer) =>
                                    <Form.Group className={'col-lg-4'}>
                                        <div style={{display: 'flex', flexDirection: 'row'}}>
                                            <div style={{whiteSpace: 'nowrap', paddingTop: 6, paddingRight: 8}}>
                                                {labelContainer}
                                            </div>
                                            <div style={{flexGrow: 1}}>
                                                {fieldContainer}
                                                {errorTextContainer}
                                            </div>
                                        </div>
                                    </Form.Group>
                                }
                            />
                            <NumberFormGroup
                                formik={f}
                                label={s.durationMinutes}
                                name={'durationMinutes'}
                                disableValidStyling
                                layout={(labelContainer, fieldContainer, errorTextContainer) =>
                                    <Form.Group className={'col-lg-4'}>
                                        <div style={{display: 'flex', flexDirection: 'row'}}>
                                            <div style={{whiteSpace: 'nowrap', paddingTop: 6, paddingRight: 8}}>
                                                {labelContainer}
                                            </div>
                                            <div style={{flexGrow: 1}}>
                                                {fieldContainer}
                                                {errorTextContainer}
                                            </div>
                                        </div>
                                    </Form.Group>
                                }
                            />
                            <div className={'col-lg-4'}>
                                <SubmitButton block formik={f}>{s.submit}</SubmitButton>
                            </div>
                        </div>
                    </form>
                }
            </Formik>
            <div style={{position: 'relative'}}>
                <input
                    className={'form-group'}
                    style={{width: '100%'}}
                    disabled={typeof clusterHistory !== 'object'}
                    type={'range'}
                    min={0}
                    max={typeof clusterHistory === 'object' ? clusterHistory.durationMinutes : 0}
                    step={typeof clusterHistory === 'object' ? clusterHistory.intervalSizeMinutes : 1}
                    value={selectedMinute}
                    onChange={e => {
                        const value = Number(e.target.value);
                        const max = typeof clusterHistory === 'object' ? clusterHistory.durationMinutes : 1;
                        setSelectedMinute(value);
                        setSelectedCluster(null);
                        setSliderHoldPosition(100 * value / max);
                    }}
                    onMouseUp={() => setSliderHoldPosition(null)}
                />
                {sliderHoldPosition != null &&
                <output
                    className={'badge badge-primary'}
                    style={{
                        display: 'inline-block',
                        position: 'absolute',
                        top: -22,
                        width: 140,
                        left: `min(max(${sliderHoldPosition}%, 70px), 100% - 70px)`,
                        transform: 'translate(-50%, 0)',
                        border: '1px solid #888F'
                    }}
                >
                    {intervalMessage}
                </output>
                }
            </div>
            <MapboxMap containerStyle={{flexGrow: 1}}>
                <MapEventListener
                    type={'click'}
                    listener={(e, map) => {
                        const features = map.queryRenderedFeatures(
                            e.point,
                            {layers: ['polygons-layer']}
                        );
                        if (features.length === 0) {
                            setSelectedCluster(null);
                            return;
                        }
                        const properties = features[features.length - 1].properties as ClusterHistoryProperties;
                        if (selectedCluster != null &&
                            properties.clusterId === selectedCluster.clusterId &&
                            properties.prediction === selectedCluster.prediction) {
                            setSelectedCluster(null);
                            return;
                        }
                        setSelectedCluster(properties);
                    }}
                />
                <MapLoadingGuard
                    value={clusterHistory}
                    defaultValue={null}
                    content={clusterHistory => clusterHistory != null &&
                        <>
                            <DropdownControl id={'layers'} title={s.layers}>
                                {arePredictionsEnabled.map((isEnabled, prediction) =>
                                    <CheckboxDropdownItem
                                        key={prediction}
                                        eventKey={`prediction ${prediction}`}
                                        value={isEnabled}
                                        onChange={v => {
                                            const newArray = arePredictionsEnabled.slice();
                                            newArray[prediction] = v;
                                            setPredictionsEnabled(newArray);
                                        }}
                                    >
                                        {s.prediction(prediction)}
                                    </CheckboxDropdownItem>
                                )}
                            </DropdownControl>
                            <LegendControl title={s.clusterHistory}>
                                <div style={{textAlign: 'right'}}>
                                    {intervalMessage}
                                </div>
                                {arePredictionsEnabled.map((isEnabled, prediction) => isEnabled &&
                                    <div key={prediction} style={{textAlign: 'right'}}>
                                        {s.prediction(prediction)}
                                        &nbsp;
                                        <div
                                            style={{
                                                display: 'inline-block',
                                                width: 12,
                                                height: 12,
                                                border: '1px solid black',
                                                margin: 0,
                                                backgroundColor: prediction === 0 ? realClusterColor :
                                                    predictedClusterColor + (
                                                        prediction === 1 ? 'aa' : (
                                                            prediction === 2 ? '88' : '55'
                                                        )
                                                    )
                                            }}
                                        />
                                    </div>
                                )}
                            </LegendControl>
                        </>
                    }
                />
                {selectedCluster != null &&
                <Popup coordinates={[selectedCluster.centroidX, selectedCluster.centroidY]}>
                    <p style={{margin: 0}}>
                        {s.prediction(selectedCluster.prediction)}
                        <br/>
                        <b>{numberToUserString(selectedCluster.strokeCount, language)}</b>
                        &nbsp;
                        {s.strokes}
                    </p>
                </Popup>
                }
                <NullGuard
                    value={selectedInterval}
                    defaultValue={emptyInterval}
                    content={internal =>
                        <>
                            <SimpleGeoJSONSource
                                id={'polygons-source'}
                                data={internal.polygons}
                            />
                            <SimpleGeoJSONSource
                                id={'centroids-source'}
                                data={internal.centroids}
                            />
                            <MapContext.Consumer>
                                {map => map != null &&
                                    <Layer
                                        id={'polygons-layer'}
                                        sourceId={'polygons-source'}
                                        type={'fill'}
                                        filter={['at', ['get', 'prediction'], ['literal', arePredictionsEnabled]]}
                                        layout={{}}
                                        paint={{
                                            'fill-color': ['case',
                                                ['<=', ['get', 'prediction'], 0], realClusterColor,
                                                predictedClusterColor
                                            ],
                                            'fill-opacity': ['case',
                                                ['<=', ['get', 'prediction'], 0], 0.5,
                                                ['<=', ['get', 'prediction'], 1], 0.35,
                                                ['<=', ['get', 'prediction'], 2], 0.2,
                                                0.1
                                            ]
                                        }}
                                        onMouseEnter={() => map.getCanvas().style.cursor = 'pointer'}
                                        onMouseLeave={() => map.getCanvas().style.removeProperty('cursor')}
                                    />
                                }
                            </MapContext.Consumer>
                            <Layer
                                sourceId={'polygons-source'}
                                type={'line'}
                                filter={['at', ['get', 'prediction'], ['literal', arePredictionsEnabled]]}
                                layout={{
                                    'line-cap': 'round'
                                }}
                                paint={{
                                    'line-width': 2,
                                    'line-color': ['case',
                                        ['<=', ['get', 'prediction'], 0], realClusterColor,
                                        predictedClusterColor
                                    ],
                                    'line-opacity': ['case',
                                        ['<=', ['get', 'prediction'], 0], 0.625,
                                        ['<=', ['get', 'prediction'], 1], 0.4375,
                                        ['<=', ['get', 'prediction'], 2], 0.25,
                                        0.125
                                    ]
                                }}
                            />
                            <Layer
                                sourceId={'centroids-source'}
                                type={'circle'}
                                filter={['at', ['get', 'prediction'], ['literal', arePredictionsEnabled]]}
                                layout={{}}
                                paint={{
                                    'circle-radius': 3,
                                    'circle-color': ['case',
                                        ['<=', ['get', 'prediction'], 0], realClusterColor,
                                        predictedClusterColor
                                    ],
                                    'circle-opacity': ['case',
                                        ['<=', ['get', 'prediction'], 0], 0.625,
                                        ['<=', ['get', 'prediction'], 1], 0.4375,
                                        ['<=', ['get', 'prediction'], 2], 0.25,
                                        0.125
                                    ]
                                }}
                            />
                        </>
                    }
                />
            </MapboxMap>
        </div>
    );
}
